import IconButton from '@/global/components/Button/IconButton'
import ImprimirButton from '@/global/components/DataGrid/components/CustomGridToolBar/ImprimirButton'
import { GridButton } from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import useDefaultFilterModel from '@/global/components/DataGrid/hooks/useDefaultFilterModel'
import useDefaultSortModel from '@/global/components/DataGrid/hooks/useDefaultSortModel'
import FilterModelPro from '@/global/components/DataGrid/utils/filterModelPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { useFilterLocaisCentroStore } from '@/global/components/Selects/filter-select'
import { hoursToMinutes, minutesToHours } from '@acme/common'
import SP, { usePeriodo } from 'global/components/Selects/SelectPeriodo'
import ContentGuide from 'global/guides/ContentGuide'
import { ListIcon, SettingsIcon } from 'lucide-react'
import React, { useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { DescontoDSREmpresas, descontoDsr } from 'service/api'
import { useStore } from 'store'
import { match } from 'ts-pattern'

const columns: GridColDefPro[] = [
    {
        field: 'Funcionario',
        headerName: 'Funcionário',
        flex: 1,
        minWidth: 200,
        valueGetter: ({ value }) => value.nome,
    },
    {
        field: 'valor',
        headerName: 'Valor descontado',
        width: 200,
        minWidth: 100,
    },
    {
        ...optionsColumn,
        field: '__detalhes',
        renderCell: ({ id }) => (
            <IconButton
                href={`${id}/detalhes`}
                title="Listar descontos detalhados"
            >
                <ListIcon />
            </IconButton>
        ),
    },
    {
        field: 'locais',
        headerName: 'Locais',
        width: 200,
        valueGetter: ({ row }) =>
            row.Funcionario.FuncionarioLocais.map(
                (e: any) => e.Local.nomeLocal,
            ).join(', '),
        hide: true,
    },
]

export default function RelatorioDescontoDSRCliente() {
    const [isImprimindo, setIsImprimindo] = useState(false)
    const [filterModel, setFilterModel] = useDefaultFilterModel()
    const [sortModel, setSortModel, sort] = useDefaultSortModel(columns)
    const [[dataInicial, dataFinal], setPeriodo] = usePeriodo()
    const empresa = useStore(state => state.company)

    const { data = [], isLoading } = descontoDsr.useFetch({
        queryParams: {
            dataInicial,
            dataFinal,
            empresaId: empresa?.id,
        },
        enabled: !!empresa,
    })

    const useFilter = useFilterLocaisCentroStore()
    const rows = data.filter(useFilter)

    const handleClick = async () => {
        setIsImprimindo(true)
        descontoDsr
            .pdf({
                data: sort(
                    rows?.filter(
                        new FilterModelPro(filterModel, columns).filter,
                    ),
                ).map(({ id }) => id),
                queryParam: {
                    dataInicial,
                    dataFinal,
                    empresaId: empresa?.id,
                },
            })
            .finally(() => {
                setIsImprimindo(false)
            })
    }

    const MemoizedFooter = React.useMemo(() => {
        if (!empresa) return () => <></>

        const filteredData =
            rows?.filter(new FilterModelPro(filterModel, columns).filter) ?? []

        const total = match(empresa.dsrExibeHoras)
            .with('S', () => {
                return minutesToHours(
                    filteredData.reduce(
                        (acc, curr) => acc + hoursToMinutes(curr.valor),
                        0,
                    ),
                )
            })
            .otherwise(() => {
                return (
                    (filteredData.reduce(
                        (acc, curr) =>
                            acc +
                            Number(String(curr.valor).replace(/\sdia/, '')),
                        0,
                    ) || 0) + ' dia(s)'
                )
            })

        return () => (
            <div style={{ flex: 1, padding: '1rem' }}>
                <span>Total descontado: {total}</span>
            </div>
        )
    }, [empresa?.dsrExibeHoras, rows, filterModel])

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header label={['Relatórios', 'Desconto de DSR']}>
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    >
                        <SP.Root
                            onChange={setPeriodo}
                            value={[dataInicial, dataFinal]}
                            allowedTypes={['competencia', 'periodo']}
                        >
                            <SP.DateSelector />
                        </SP.Root>
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    loading={isLoading}
                    columns={columns}
                    rows={rows}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    components={{
                        Toolbar: () => (
                            <>
                                <GridButton
                                    href={`/cadastros/empresas/${empresa?.id}/editar?aba=dsr`}
                                    label="Configurar"
                                    startIcon={<SettingsIcon />}
                                />
                                <div style={{ flex: 1 }} />
                                <ImprimirButton
                                    handleClick={handleClick}
                                    isImprimindo={isImprimindo}
                                />
                            </>
                        ),
                        Footer: MemoizedFooter,
                    }}
                    tableId="desconto-dsr-cliente"
                    ToolbarProps={{
                        exportButtonProps: {
                            fileName: 'exportacao-dsr-cliente',
                        },
                        showVisibilityButton: true,
                    }}
                />
            </ContentGuide.Card>
            <Outlet context={{ data: rows }} />
        </ContentGuide>
    )
}

export function useRelatorioDescontoDsrOutletContext() {
    return useOutletContext<{ data?: DescontoDSREmpresas[] }>()
}
